// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import AddressModal from './AddressModal/AddressModal';
import { getLocalizedCountry } from '../../../local-values/utils/countries';
import Modal from '../../../../elements/Modal/Modal';
import Loading from '../../../../elements/Loading/Loading';
import DotsMenu from '../../../../elements/DotsMenu/DotsMenu';

import style from './SavedAddresses.scss';
import commonStyle from '../../PreferencesViewCommon.scss';
import boxStyle from '../../../../assets/styles/box.scss';
import icons from '../../../../assets/styles/icons.scss';
import modalStyle from '../../../../assets/styles/modal.scss';
import buttonStyle from '../../../../assets/styles/button.scss';

import type { Address } from '../../../../entities';
import type { AddressRequest } from '../api';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  +postAddress: (address: AddressRequest) => void;
  +editAddress: (address: AddressRequest, addressId: string) => void;
  +deleteAddress: (id: string) => void;
  +addresses?: Address[];
  +closeAddressModal: boolean;
  +successDelete?: boolean;
  +isPostingAddress?: boolean;
  +isPuttingAddress?: boolean;
  +isDeletingAddress?: boolean;
  +resetAlertState: () => void;
  +localValues: LocalValuesState;
};

type State = {
  addressModalVisible: boolean;
  deleteModalVisible: boolean;
  cleanModalState?: boolean;
  address?: Address;
  addressId: string;
};

const messages = defineMessages({
  title: {
    id: 'preferences.savedAddresses.document.title',
  },
  description: {
    id: 'preferences.savedAddresses.document.description',
  },
  savedAddresses: {
    title: {
      id: 'preferences.savedAddresses.title',
    },
    subtitle: {
      id: 'preferences.savedAddresses.subtitle',
    },
  },
  addNewAddress: {
    id: 'preferences.savedAddresses.addNewAddress',
  },
  deleteWarning: {
    id: 'preferences.savedAddresses.deleteWarning',
  },
  button: {
    cancel: {
      id: 'preferences.savedAddresses.button.cancel',
    },
    delete: {
      id: 'preferences.savedAddresses.button.delete',
    },
  },
});

export default class SavedAddresses extends Component<Props, State> {
  state = {
    addressModalVisible: false,
    deleteModalVisible: false,
    cleanModalState: false,
    address: undefined,
    addressId: '',
  };

  componentWillReceiveProps(nextProps: Props) {
    const { addressModalVisible } = this.state;
    if (nextProps.closeAddressModal && addressModalVisible) {
      this.setState({
        addressModalVisible: false,
        cleanModalState: true,
        address: null,
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { deleteModalVisible } = this.state;
    const { successDelete } = this.props;
    if (prevProps.successDelete !== successDelete && successDelete && deleteModalVisible) {
      this.setState({
        deleteModalVisible: false,
      });
    }
  }

  componentWillUnmount() {
    const { resetAlertState } = this.props;
    resetAlertState();
  }

  handleOpenAddressModal() {
    this.setState({
      addressModalVisible: true,
      cleanModalState: false,
    });
  }

  handleCloseAddressModal() {
    this.setState({
      addressModalVisible: false,
      cleanModalState: true,
      address: null,
    });
  }

  handleOpenDeleteModal(e: Event, addressId: string) {
    this.setState({
      deleteModalVisible: true,
      addressId,
    });
  }

  handleCloseDeleteModal() {
    this.setState({
      deleteModalVisible: false,
    });
  }

  handleEdit(e: Event, address: Address) {
    this.setState({
      addressModalVisible: true,
      cleanModalState: false,
      address,
    });
  }

  handleDelete(addressId: string) {
    const { deleteAddress } = this.props;
    deleteAddress(addressId);
  }

  buildItem(address: Address, key: string) {
    const { localValues } = this.props;
    const countryIntl = getLocalizedCountry(address.country.code, this.context.intl.formatMessage, localValues.countriesMap);
    const menuOptions = [
      {
        labelIntl: 'preferences.savedAddresses.edit',
        icon: icons.iconEditMedium,
        color: 'lightBlue',
        handler: (e: Event) => this.handleEdit(e, address),
      },
      {
        labelIntl: 'preferences.savedAddresses.delete',
        icon: icons.iconTrashcanMedium,
        color: 'red',
        handler: (e: Event) => this.handleOpenDeleteModal(e, address.id),
      }
    ];
    return (
      <article key={key} className={style.box}>
        <p className={style.name}>{address.name}</p>
        <p>{address.address1}, {address.address2}</p>
        <p>{address.admin2.name}, {address.admin1.name}, {countryIntl} - {address.zipCode}</p>
        <p>{address.phone}</p>
        <DotsMenu orientation="left"
                  buttonStyle={style.dotsButton}
                  menuOptions={menuOptions}/>
      </article>);
  }

  render() {
    const {
      addresses, postAddress, editAddress, isPostingAddress, isPuttingAddress, isDeletingAddress,
      localValues,
    } = this.props;
    const { addressModalVisible, deleteModalVisible, cleanModalState, address, addressId } = this.state;
    const addressesContent = addresses && addresses.map((address: Address, n: number) =>
      this.buildItem(address, `${n}`));

    return [
      <Helmet key="helmet">
        <title>{this.context.intl.formatMessage(messages.title)}</title>
        <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
      </Helmet>,
      <section key="addresses" className={classNames(boxStyle.box, commonStyle.contentWrap, style.contentWrapper)}>
        <FormattedMessage id={messages.savedAddresses.title.id}>{
          txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
        }</FormattedMessage>
        <FormattedMessage id={messages.savedAddresses.subtitle.id}>{
          txt => <p className={boxStyle.subtitle}>{txt}</p>
        }</FormattedMessage>
        <section className={style.boxesWrap}>
          <article className={classNames(style.box, style.firstBox)}
                   onClick={this.handleOpenAddressModal.bind(this)}>
            <div className={style.iconContainer}>
              <i className={icons.iconLocationSquareMedium}/>
            </div>
            <FormattedMessage id={messages.addNewAddress.id}>{
              txt => <p>{txt}</p>
            }</FormattedMessage>
          </article>
          {addressesContent}
        </section>
      </section>,
      <AddressModal key="addressModal"
                    handleClose={this.handleCloseAddressModal.bind(this)}
                    addressModalVisible={addressModalVisible}
                    cleanModalState={cleanModalState}
                    address={address}
                    postAddress={postAddress}
                    editAddress={editAddress}
                    isPosting={isPostingAddress}
                    isPutting={isPuttingAddress}
                    localValues={localValues}/>,
      <Modal key="deleteModal"
             open={deleteModalVisible}
             height={180}
             handleClose={this.handleCloseDeleteModal.bind(this)}>
        {isDeletingAddress && <Loading type="sk-three-bounce"
                                       className={commonStyle.modalLoading}
                                       number={3}/>}
        {!isDeletingAddress && [
          <i key="icon" className={classNames(icons.symbolExclamationCircle, modalStyle.title)}/>,
          <FormattedMessage id={messages.deleteWarning.id} key="content">{
            txt => <p className={modalStyle.content}>{txt}</p>
          }</FormattedMessage>,
          <section className={modalStyle.buttonsWrap} key="wrap">
            <FormattedMessage id={messages.button.cancel.id}>{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.darkGrey)}
                             onClick={this.handleCloseDeleteModal.bind(this)}
              >{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id={messages.button.delete.id}>{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.red)}
                             onClick={this.handleDelete.bind(this, addressId)}
              >{txt}</button>
            }</FormattedMessage>
          </section>,
        ]}
      </Modal>,
    ];
  }
}

SavedAddresses.propTypes = {
  postAddress: PropTypes.func.isRequired,
  editAddress: PropTypes.func.isRequired,
  deleteAddress: PropTypes.func.isRequired,
  addresses: PropTypes.array,
  closeAddressModal: PropTypes.bool,
  isPostingAddress: PropTypes.bool,
  isPuttingAddress: PropTypes.bool,
  isDeletingAddress: PropTypes.bool,
  resetAlertState: PropTypes.func.isRequired,
  localValues: PropTypes.object.isRequired,
};

SavedAddresses.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/saved-addresses/components/SavedAddresses.jsx